/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Shared utilities for parsing and formatting user stories from markdown
 * Used by both the backend user story manager tool and the product app
 */

import * as fs from 'fs';
import * as path from 'path';
import { EPIC_HEADER_PATTERN, STORY_HEADER_REGEX, isValidEpicId } from './id-patterns';
import { normalizeLineEndings } from './issues';
import {
  UserStory,
  Epic,
  UserStoryStatus,
  ParsedUserStories,
  USER_STORY_STATUSES,
  AcceptanceCriterion,
  AcceptanceCriterionStatus,
  ACCEPTANCE_CRITERION_STATUSES,
  DEFAULT_ACCEPTANCE_CRITERION_STATUS,
  normalizeAcceptanceCriteria,
  formatAcceptanceCriterion,
  formatAcceptanceCriteriaList,
} from '../types/user-stories';

// Re-export types and helpers from the shared types module so callers can import them
// from a single location (`@shared/utils/user-stories`) when they are also using the
// other utilities defined in this file.
export type { UserStory, Epic, UserStoryStatus, ParsedUserStories, AcceptanceCriterion, AcceptanceCriterionStatus };
export {
  USER_STORY_STATUSES,
  ACCEPTANCE_CRITERION_STATUSES,
  DEFAULT_ACCEPTANCE_CRITERION_STATUS,
  normalizeAcceptanceCriteria,
  formatAcceptanceCriterion,
  formatAcceptanceCriteriaList,
};

function getDefaultUserStoriesDataRoot(): string | null {
  if (typeof process !== 'undefined' && typeof process.cwd === 'function') {
    return path.join(process.cwd(), 'data', 'product');
  }
  return null;
}

function resolveUserStoriesDir(baseDataDir?: string): string {
  const dataRoot = baseDataDir ?? getDefaultUserStoriesDataRoot();
  if (!dataRoot) {
    throw new Error('Base data directory must be provided when running outside of Node.js.');
  }
  return path.join(dataRoot, 'user-stories');
}

/**
 * Parses user stories and epics from markdown content
 */
export function parseUserStoriesFromMarkdown(content: string, filePath?: string): ParsedUserStories {
  const stories: UserStory[] = [];
  const epics: Epic[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];

  // Normalize line endings to handle Windows CRLF files
  const lines = normalizeLineEndings(content).split('\n');
  let currentSection = '';
  let currentStory: Partial<UserStory> | null = null;
  let currentEpic: Partial<Epic> | null = null;
  let acceptanceCriteria: AcceptanceCriterion[] = [];
  let inAcceptanceCriteria = false;
  let inEpicDescription = false;
  let epicDescription = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Look for epic headers like "## ADM-PLT: Platform Administration"
    const epicHeaderMatch = line.match(new RegExp(EPIC_HEADER_PATTERN, 'i'));
    if (epicHeaderMatch) {
      // Save previous epic if exists
      if (currentEpic && currentEpic.id && currentEpic.title) {
        epics.push({
          ...currentEpic,
          description: epicDescription.trim(),
          userStoryIds: [],
          section: currentSection,
          filePath
        } as Epic);
      }

      // Start new epic
      currentEpic = {
        id: epicHeaderMatch[1],
        title: epicHeaderMatch[2],
      };
      epicDescription = '';
      inEpicDescription = true;
      continue;
    }

    // Track current section
    if (line.startsWith('# ') || (line.startsWith('## ') && line.includes('User Stories'))) {
      currentSection = line.replace(/^#+ /, '');
      continue;
    }

    // Look for epic description lines
    if (inEpicDescription && line.match(/^-\s*\*\*Description:\*\*/)) {
      const descMatch = line.match(/^-\s*\*\*Description:\*\*\s*(.+)$/);
      if (descMatch) {
        epicDescription = descMatch[1];
      }
      inEpicDescription = false;
      continue;
    }

    // Look for epic status lines (but not when we're in acceptance criteria mode)
    if (currentEpic && !inAcceptanceCriteria && line.match(/^-\s*\*\*Status:\*\*/)) {
      const statusMatch = line.match(/^-\s*\*\*Status:\*\*\s*(.+)$/);
      if (statusMatch) {
        currentEpic.status = statusMatch[1] as UserStoryStatus;
      }
      continue;
    }

    // Look for epic product lines (but not when we're in acceptance criteria mode)
    if (currentEpic && !inAcceptanceCriteria && line.match(/^-\s*\*\*Product:\*\*/)) {
      const productMatch = line.match(/^-\s*\*\*Product:\*\*\s*(.+)$/);
      if (productMatch) {
        currentEpic.product = productMatch[1].trim() || undefined;
      }
      continue;
    }

    // Look for story headers like **ADM-PLT-001: Initial Platform Setup**
    // Uses shared STORY_HEADER_REGEX which allows alphanumeric characters in epic ID
    const storyHeaderMatch = line.match(STORY_HEADER_REGEX);
    if (storyHeaderMatch) {
      // Save previous story if exists
      if (currentStory && currentStory.id && currentStory.title) {
        const story = {
          ...currentStory,
          acceptanceCriteria,
          section: currentSection,
          epicId: undefined,
          filePath
        } as UserStory;
        stories.push(story);
      }

      // Start new story
      currentStory = {
        id: storyHeaderMatch[1],
        title: storyHeaderMatch[2],
        role: '',
        goal: '',
        benefit: '',
      };
      acceptanceCriteria = [];
      inAcceptanceCriteria = false;
      continue;
    }

    if (!currentStory) continue;

    // Parse acceptance criteria items first - this must come before story component parsing
    // because trimmed AC status sub-bullets like "- **Status:** Ready For Review" would 
    // incorrectly match the story status regex if not handled here first
    if (inAcceptanceCriteria) {
      // Use the original untrimmed line to detect indentation levels
      const originalLine = lines[i];
      
      // Check for release sub-bullet (more indented, starts with "- **Release:**")
      // This should be associated with the previous criterion
      const releaseMatch = originalLine.match(/^\s{4,}-\s*\*\*Release:\*\*\s*(.+)$/);
      if (releaseMatch && acceptanceCriteria.length > 0) {
        // Attach release to the last criterion
        acceptanceCriteria[acceptanceCriteria.length - 1].release = releaseMatch[1].trim();
        continue;
      }
      
      // Check for status sub-bullet (more indented, starts with "- **Status:**")
      // This should be associated with the previous criterion
      const statusMatch = originalLine.match(/^\s{4,}-\s*\*\*Status:\*\*\s*(.+)$/);
      if (statusMatch && acceptanceCriteria.length > 0) {
        // Attach status to the last criterion
        acceptanceCriteria[acceptanceCriteria.length - 1].status = statusMatch[1].trim() as AcceptanceCriterionStatus;
        continue;
      }
      
      // Check for main criterion bullet (2-space indent from acceptance criteria header)
      const criteriaMatch = originalLine.match(/^\s{2}-\s+(.+)$/);
      if (criteriaMatch) {
        // Make sure this isn't a release or status sub-bullet that somehow matched
        const text = criteriaMatch[1];
        if (!text.startsWith('**Release:**') && !text.startsWith('**Status:**')) {
          acceptanceCriteria.push({ text, status: DEFAULT_ACCEPTANCE_CRITERION_STATUS });
        }
        continue;
      }
      
      // End of acceptance criteria if we hit an empty line or next story component
      if (line === '' || line.startsWith('- **')) {
        inAcceptanceCriteria = false;
      } else {
        // Still in acceptance criteria mode but didn't match any pattern, continue to next line
        continue;
      }
    }

    // Parse story components (only reached when not in acceptance criteria or just exited it)
    const roleMatch = line.match(/^-\s*\*\*As an?\*\*\s+(.+)$/);
    if (roleMatch) {
      currentStory.role = roleMatch[1];
      continue;
    }

    const goalMatch = line.match(/^-\s*\*\*I want to\*\*\s+(.+)$/);
    if (goalMatch) {
      currentStory.goal = goalMatch[1];
      continue;
    }

    const benefitMatch = line.match(/^-\s*\*\*So that\*\*\s+(.+)$/);
    if (benefitMatch) {
      currentStory.benefit = benefitMatch[1];
      continue;
    }

    // Parse story status
    const storyStatusMatch = line.match(/^-\s*\*\*Status:\*\*\s+(.+)$/);
    if (storyStatusMatch) {
      currentStory.status = storyStatusMatch[1] as UserStoryStatus;
      continue;
    }

    // Parse story links
    const linksMatch = line.match(/^-\s*\*\*Links:\*\*\s+(.+)$/);
    if (linksMatch) {
      const linksStr = linksMatch[1].trim();
      currentStory.links = linksStr ? linksStr.split(',').map(s => s.trim()).filter(s => s) : [];
      continue;
    }

    // Parse story product
    const productMatch = line.match(/^-\s*\*\*Product:\*\*\s+(.+)$/);
    if (productMatch) {
      currentStory.product = productMatch[1].trim() || undefined;
      continue;
    }

    // Check for acceptance criteria section
    if (line.match(/^-\s*\*\*Acceptance Criteria:\*\*$/)) {
      inAcceptanceCriteria = true;
      continue;
    }
  }

  // Save last story
  if (currentStory && currentStory.id && currentStory.title) {
    const story = {
      ...currentStory,
      acceptanceCriteria,
      section: currentSection,
      epicId: undefined,
      filePath
    } as UserStory;
    stories.push(story);
  }

  // Save last epic
  if (currentEpic && currentEpic.id && currentEpic.title) {
    epics.push({
      ...currentEpic,
      description: epicDescription.trim(),
      userStoryIds: [],
      section: currentSection,
      filePath
    } as Epic);
  }

  // Populate userStoryIds for all epics based on the stories array
  epics.forEach(epic => {
    epic.userStoryIds = stories.filter(s => s.id.startsWith(epic.id + '-')).map(s => s.id);
  });

  // Assign epic IDs to stories based on ID prefix matching
  stories.forEach(story => {
    const storyEpicId = epics.find(epic => story.id.startsWith(epic.id + '-'))?.id;
    story.epicId = storyEpicId;
  });

  // Validate parsed stories
  stories.forEach((story) => {
    if (!story.role) {
      warnings.push(`Story ${story.id}: Missing role information`);
    }
    if (!story.goal) {
      warnings.push(`Story ${story.id}: Missing goal information`);
    }
    if (!story.benefit) {
      warnings.push(`Story ${story.id}: Missing benefit information`);
    }
    if (story.acceptanceCriteria.length === 0) {
      warnings.push(`Story ${story.id}: No acceptance criteria found`);
    }
  });

  // Validate parsed epics
  epics.forEach((epic) => {
    if (!epic.description) {
      warnings.push(`Epic ${epic.id}: Missing description`);
    }
    if (epic.userStoryIds.length === 0) {
      warnings.push(`Epic ${epic.id}: No user stories found`);
    }
  });

  return {
    stories,
    epics,
    errors,
    warnings
  };
}

/**
 * Formats a single user story as markdown
 */
export function formatUserStoryAsMarkdown(story: UserStory): string {
  let markdown = `**${story.id}: ${story.title}**\n`;
  markdown += `- **As a** ${story.role}\n`;
  markdown += `- **I want to** ${story.goal}\n`;
  markdown += `- **So that** ${story.benefit}\n`;
  markdown += `- **Status:** ${story.status || 'Not Started'}\n`;
  
  // Add links if present
  if (story.links && story.links.length > 0) {
    // Normalize links to array (handle case where LLM passes a string instead of array)
    const linksArray = Array.isArray(story.links) 
      ? story.links 
      : String(story.links).split(',').map(s => s.trim()).filter(s => s);
    if (linksArray.length > 0) {
      markdown += `- **Links:** ${linksArray.join(', ')}\n`;
    }
  }
  
  // Add product if present
  if (story.product) {
    markdown += `- **Product:** ${story.product}\n`;
  }
  
  markdown += `- **Acceptance Criteria:**\n`;

  story.acceptanceCriteria.forEach(criterion => {
    markdown += `  - ${criterion.text}\n`;
    if (criterion.release) {
      markdown += `    - **Release:** ${criterion.release}\n`;
    }
    // Only write status if it's not the default 'Required' status
    if (criterion.status && criterion.status !== DEFAULT_ACCEPTANCE_CRITERION_STATUS) {
      markdown += `    - **Status:** ${criterion.status}\n`;
    }
  });
  
  return markdown;
}

/**
 * Formats a single epic as markdown
 */
export function formatEpicAsMarkdown(epic: Epic): string {
  let markdown = `## ${epic.id}: ${epic.title}\n`;
  markdown += `- **Description:** ${epic.description}\n`;
  markdown += `- **Status:** ${epic.status || 'Not Started'}\n`;
  
  // Add product if present
  if (epic.product) {
    markdown += `- **Product:** ${epic.product}\n`;
  }
  
  markdown += `- **User Stories:**\n\n`;
  return markdown;
}

/**
 * Generates complete markdown content from epics and stories
 */
export function generateMarkdownFromUserStories(
  epics: Epic[],
  stories: UserStory[],
  sectionTitle: string = 'User Stories'
): string {
  let content = `# ${sectionTitle}\n\n`;

  // Group stories by epic
  epics.forEach(epic => {
    content += formatEpicAsMarkdown(epic);
    
    // Add stories for this epic
    const epicStories = stories.filter(s => s.epicId === epic.id);
    epicStories.forEach(story => {
      content += formatUserStoryAsMarkdown(story);
      content += '\n';
    });
  });

  return content;
}

/**
 * Gets all user story files in the data/product/user-stories directory
 */
export function getUserStoryFiles(baseDataDir?: string): string[] {
  const userStoriesDir = resolveUserStoriesDir(baseDataDir);
  const files: string[] = [];

  function walkDir(dir: string) {
    const entries = fs.readdirSync(dir, { withFileTypes: true });
    
    for (const entry of entries) {
      const fullPath = path.join(dir, entry.name);
      
      if (entry.isDirectory()) {
        walkDir(fullPath);
      } else if (entry.isFile() && entry.name.endsWith('.md')) {
        files.push(fullPath);
      }
    }
  }

  try {
    walkDir(userStoriesDir);
  } catch (error) {
    // Directory doesn't exist or can't be read
    return [];
  }

  return files;
}

/**
 * Reads and parses all user story files
 */
export function readAllUserStories(baseDataDir?: string): ParsedUserStories {
  const files = getUserStoryFiles(baseDataDir);
  const allStories: UserStory[] = [];
  const allEpics: Epic[] = [];
  const allErrors: string[] = [];
  const allWarnings: string[] = [];

  for (const filePath of files) {
    try {
      const content = fs.readFileSync(filePath, 'utf8');
      const parsed = parseUserStoriesFromMarkdown(content, filePath);
      
      allStories.push(...parsed.stories);
      allEpics.push(...parsed.epics);
      allErrors.push(...parsed.errors);
      allWarnings.push(...parsed.warnings);
    } catch (error) {
      allErrors.push(`Failed to read ${filePath}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  return {
    stories: allStories,
    epics: allEpics,
    errors: allErrors,
    warnings: allWarnings
  };
}

/**
 * Writes user stories and epics back to a file
 */
export function writeUserStoryFile(
  filePath: string,
  epics: Epic[],
  stories: UserStory[],
  sectionTitle?: string
): void {
  // Read existing file to preserve section title if not provided
  let title = sectionTitle;
  if (!title) {
    try {
      const existingContent = fs.readFileSync(filePath, 'utf8');
      const titleMatch = existingContent.match(/^# (.+)$/m);
      title = titleMatch ? titleMatch[1] : 'User Stories';
    } catch {
      title = 'User Stories';
    }
  }

  const content = generateMarkdownFromUserStories(epics, stories, title);
  
  // Ensure directory exists
  const dir = path.dirname(filePath);
  fs.mkdirSync(dir, { recursive: true });
  
  fs.writeFileSync(filePath, content, 'utf8');
}

/**
 * Updates a user story in its file
 */
export function updateUserStory(storyId: string, updates: Partial<UserStory>, options?: { baseDataDir?: string }): UserStory {
  const allData = readAllUserStories(options?.baseDataDir);
  const story = allData.stories.find(s => s.id === storyId);
  
  if (!story) {
    throw new Error(`User story ${storyId} not found`);
  }
  
  if (!story.filePath) {
    throw new Error(`User story ${storyId} has no associated file path`);
  }

  // Read the file containing this story
  const content = fs.readFileSync(story.filePath, 'utf8');
  const parsed = parseUserStoriesFromMarkdown(content, story.filePath);
  
  // Update the story
  const storyIndex = parsed.stories.findIndex(s => s.id === storyId);
  if (storyIndex === -1) {
    throw new Error(`User story ${storyId} not found in file ${story.filePath}`);
  }
  
  parsed.stories[storyIndex] = {
    ...parsed.stories[storyIndex],
    ...updates,
    id: story.id, // Preserve ID
  };
  
  // Write back to file
  writeUserStoryFile(story.filePath, parsed.epics, parsed.stories);
  
  return parsed.stories[storyIndex];
}

/**
 * Updates an epic in its file
 */
export function updateEpic(epicId: string, updates: Partial<Epic>, options?: { baseDataDir?: string }): Epic {
  const allData = readAllUserStories(options?.baseDataDir);
  const epic = allData.epics.find(e => e.id === epicId);
  
  if (!epic) {
    throw new Error(`Epic ${epicId} not found`);
  }
  
  if (!epic.filePath) {
    throw new Error(`Epic ${epicId} has no associated file path`);
  }

  // Read the file containing this epic
  const content = fs.readFileSync(epic.filePath, 'utf8');
  const parsed = parseUserStoriesFromMarkdown(content, epic.filePath);
  
  // Update the epic
  const epicIndex = parsed.epics.findIndex(e => e.id === epicId);
  if (epicIndex === -1) {
    throw new Error(`Epic ${epicId} not found in file ${epic.filePath}`);
  }
  
  parsed.epics[epicIndex] = {
    ...parsed.epics[epicIndex],
    ...updates,
    id: epic.id, // Preserve ID
    userStoryIds: parsed.epics[epicIndex].userStoryIds, // Preserve story IDs
  };
  
  // Write back to file
  writeUserStoryFile(epic.filePath, parsed.epics, parsed.stories);
  
  return parsed.epics[epicIndex];
}

/**
 * Generates next user story ID for an epic
 */
export function generateNextStoryId(epicId: string, options?: { baseDataDir?: string }): string {
  const allData = readAllUserStories(options?.baseDataDir);
  const epicStories = allData.stories.filter(s => s.epicId === epicId);
  
  const existingNumbers = epicStories
    .map(s => {
      const match = s.id.match(new RegExp(`${epicId}-(\\d+)$`));
      return match ? parseInt(match[1]) : 0;
    })
    .filter(n => n > 0);
  
  const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
  return `${epicId}-${nextNumber.toString().padStart(3, '0')}`;
}

/**
 * Infers the category folder from an epic ID
 * @param epicId - Epic ID in format CATEGORY-SUBCATEGORY (e.g., 'PST-ISM')
 * @returns Category folder name (e.g., 'product-student')
 */
function inferCategoryFromEpicId(epicId: string): string {
  const categoryMap: Record<string, string> = {
    'ADM': 'admin',
    'TCH': 'teacher',
    'LRN': 'learner',
    'CRM': 'curriculum-manager',
    'PST': 'product-student',
    'TEC': 'technical',
    'CRS': 'cross-functional'
  };

  const prefix = epicId.split('-')[0];
  return categoryMap[prefix] || 'other';
}

/**
 * Infers a human-readable title from an epic ID
 * @param epicId - Epic ID in format CATEGORY-SUBCATEGORY (e.g., 'PST-ISM')
 * @returns Human-readable title (e.g., 'Issue Management')
 */
function inferTitleFromEpicId(epicId: string): string {
  const parts = epicId.split('-');
  if (parts.length < 2) return epicId;
  
  // Known abbreviation mappings
  const abbreviationMap: Record<string, string> = {
    'ISM': 'Issue Management',
    'PLT': 'Platform',
    'USR': 'User Management',
    'AUTH': 'Authentication',
    'CRS': 'Course',
    'LSN': 'Lesson',
    'AST': 'Assessment',
    'RPT': 'Reporting',
    'INT': 'Integration',
    'LLM': 'LLM Integration',
    'STG': 'Storage',
    'SEC': 'Security'
  };
  
  const subcategory = parts[1];
  
  // Check if we have a known mapping
  if (abbreviationMap[subcategory]) {
    return abbreviationMap[subcategory];
  }
  
  // Otherwise, just capitalize it as a fallback
  return subcategory.charAt(0) + subcategory.slice(1).toLowerCase();
}

/**
 * Creates a new epic in the appropriate file
 * @param epicId - Epic ID in format CATEGORY-SUBCATEGORY (e.g., 'PST-ISM')
 * @param epicData - Epic data (title, description, status, product)
 * @param category - Category folder name (e.g., 'product-student', 'admin')
 * @param fileName - Optional file name (defaults to category name)
 */
export function createEpic(
  epicId: string,
  epicData: Omit<Epic, 'id' | 'userStoryIds' | 'filePath'>,
  category: string,
  fileName?: string,
  options?: { baseDataDir?: string }
): Epic {
  // Validate epic ID format (should be CATEGORY-SUBCATEGORY)
  if (!isValidEpicId(epicId)) {
    throw new Error(`Invalid epic ID format: ${epicId}. Expected format: CATEGORY-SUBCATEGORY (e.g., 'PST-ISM')`);
  }

  // Check if epic already exists
  const allData = readAllUserStories(options?.baseDataDir);
  const existingEpic = allData.epics.find(e => e.id === epicId);
  if (existingEpic) {
    throw new Error(`Epic ${epicId} already exists`);
  }

  // Determine file path
  const userStoriesDir = resolveUserStoriesDir(options?.baseDataDir);
  const categoryDir = path.join(userStoriesDir, category);
  const actualFileName = fileName || category;
  const filePath = path.join(categoryDir, `${actualFileName}.md`);

  // Ensure directory exists
  if (!fs.existsSync(categoryDir)) {
    fs.mkdirSync(categoryDir, { recursive: true });
  }

  // Create new epic
  const newEpic: Epic = {
    id: epicId,
    title: epicData.title,
    description: epicData.description,
    userStoryIds: [],
    status: epicData.status || 'Not Started',
    product: epicData.product,
    section: epicData.section,
    filePath
  };

  // Read existing file content if it exists
  let existingEpics: Epic[] = [];
  let existingStories: UserStory[] = [];
  
  if (fs.existsSync(filePath)) {
    const content = fs.readFileSync(filePath, 'utf8');
    const parsed = parseUserStoriesFromMarkdown(content, filePath);
    existingEpics = parsed.epics;
    existingStories = parsed.stories;
  }

  // Add new epic
  existingEpics.push(newEpic);

  // Write to file
  writeUserStoryFile(filePath, existingEpics, existingStories, epicData.section);

  return newEpic;
}

/**
 * Creates a new user story in an epic
 * If the epic doesn't exist, it will be created automatically
 */
export function createUserStory(
  epicId: string,
  storyData: Omit<UserStory, 'id' | 'epicId' | 'filePath'>,
  options?: { baseDataDir?: string }
): UserStory {
  const allData = readAllUserStories(options?.baseDataDir);
  let epic = allData.epics.find(e => e.id === epicId);
  
  // Auto-create epic if it doesn't exist
  if (!epic) {
    const category = inferCategoryFromEpicId(epicId);
    const title = inferTitleFromEpicId(epicId);
    
    epic = createEpic(
      epicId,
      {
        title,
        description: `User stories for ${title}`,
        status: 'Not Started',
        product: storyData.product
      },
      category,
      undefined,
      { baseDataDir: options?.baseDataDir }
    );
  }
  
  if (!epic.filePath) {
    throw new Error(`Epic ${epicId} has no associated file path`);
  }

  // Generate new story ID
  const storyId = generateNextStoryId(epicId, { baseDataDir: options?.baseDataDir });
  
  // Read the file containing this epic
  const content = fs.readFileSync(epic.filePath, 'utf8');
  const parsed = parseUserStoriesFromMarkdown(content, epic.filePath);
  
  // Create new story
  const newStory: UserStory = {
    ...storyData,
    id: storyId,
    epicId: epicId,
    filePath: epic.filePath,
    status: storyData.status || 'Not Started'
  };
  
  // Add to stories array
  parsed.stories.push(newStory);
  
  // Update epic's userStoryIds
  const epicIndex = parsed.epics.findIndex(e => e.id === epicId);
  if (epicIndex !== -1) {
    parsed.epics[epicIndex].userStoryIds.push(storyId);
  }
  
  // Write back to file
  writeUserStoryFile(epic.filePath, parsed.epics, parsed.stories);
  
  return newStory;
}

/**
 * Creates a new user story with a custom ID in an epic
 * If the epic doesn't exist, it will be created automatically
 */
export function createUserStoryWithId(
  epicId: string,
  customStoryId: string,
  storyData: Omit<UserStory, 'id' | 'epicId' | 'filePath'>,
  options?: { baseDataDir?: string }
): UserStory {
  const allData = readAllUserStories(options?.baseDataDir);
  let epic = allData.epics.find(e => e.id === epicId);
  
  // Auto-create epic if it doesn't exist
  if (!epic) {
    const category = inferCategoryFromEpicId(epicId);
    const title = inferTitleFromEpicId(epicId);
    
    epic = createEpic(
      epicId,
      {
        title,
        description: `User stories for ${title}`,
        status: 'Not Started',
        product: storyData.product
      },
      category,
      undefined,
      { baseDataDir: options?.baseDataDir }
    );
  }
  
  if (!epic.filePath) {
    throw new Error(`Epic ${epicId} has no associated file path`);
  }

  // Validate that the custom ID matches the epic
  if (!customStoryId.startsWith(`${epicId}-`)) {
    throw new Error(`Story ID ${customStoryId} doesn't match epic ${epicId}`);
  }

  // Check if story ID already exists
  const existingStory = allData.stories.find(s => s.id === customStoryId);
  if (existingStory) {
    throw new Error(`Story with ID ${customStoryId} already exists`);
  }
  
  // Read the file containing this epic
  const content = fs.readFileSync(epic.filePath, 'utf8');
  const parsed = parseUserStoriesFromMarkdown(content, epic.filePath);
  
  // Create new story with custom ID
  const newStory: UserStory = {
    ...storyData,
    id: customStoryId,
    epicId: epicId,
    filePath: epic.filePath,
    status: storyData.status || 'Not Started'
  };
  
  // Add to stories array
  parsed.stories.push(newStory);
  
  // Update epic's userStoryIds
  const epicIndex = parsed.epics.findIndex(e => e.id === epicId);
  if (epicIndex !== -1) {
    parsed.epics[epicIndex].userStoryIds.push(customStoryId);
  }
  
  // Write back to file
  writeUserStoryFile(epic.filePath, parsed.epics, parsed.stories);
  
  return newStory;
}

/**
 * Deletes a user story from its file
 * Returns the deleted story
 */
export function deleteUserStory(storyId: string, options?: { baseDataDir?: string }): UserStory {
  const allData = readAllUserStories(options?.baseDataDir);
  const story = allData.stories.find(s => s.id === storyId);
  
  if (!story) {
    throw new Error(`User story ${storyId} not found`);
  }
  
  if (!story.filePath) {
    throw new Error(`User story ${storyId} has no associated file path`);
  }

  // Read the file containing this story
  const content = fs.readFileSync(story.filePath, 'utf8');
  const parsed = parseUserStoriesFromMarkdown(content, story.filePath);
  
  // Find and remove the story
  const storyIndex = parsed.stories.findIndex(s => s.id === storyId);
  if (storyIndex === -1) {
    throw new Error(`User story ${storyId} not found in file ${story.filePath}`);
  }
  
  const deletedStory = parsed.stories[storyIndex];
  parsed.stories.splice(storyIndex, 1);
  
  // Update epic's userStoryIds
  if (story.epicId) {
    const epicIndex = parsed.epics.findIndex(e => e.id === story.epicId);
    if (epicIndex !== -1) {
      parsed.epics[epicIndex].userStoryIds = parsed.epics[epicIndex].userStoryIds.filter(id => id !== storyId);
    }
  }
  
  // Write back to file
  writeUserStoryFile(story.filePath, parsed.epics, parsed.stories);
  
  return deletedStory;
}

/**
 * Deletes an epic and optionally all its stories from its file
 * Returns the deleted epic and optionally the deleted stories
 */
export function deleteEpic(
  epicId: string,
  options?: { baseDataDir?: string; deleteStories?: boolean }
): { epic: Epic; stories?: UserStory[] } {
  const allData = readAllUserStories(options?.baseDataDir);
  const epic = allData.epics.find(e => e.id === epicId);
  
  if (!epic) {
    throw new Error(`Epic ${epicId} not found`);
  }
  
  if (!epic.filePath) {
    throw new Error(`Epic ${epicId} has no associated file path`);
  }

  // Read the file containing this epic
  const content = fs.readFileSync(epic.filePath, 'utf8');
  const parsed = parseUserStoriesFromMarkdown(content, epic.filePath);
  
  // Find the epic
  const epicIndex = parsed.epics.findIndex(e => e.id === epicId);
  if (epicIndex === -1) {
    throw new Error(`Epic ${epicId} not found in file ${epic.filePath}`);
  }
  
  const deletedEpic = parsed.epics[epicIndex];
  
  // Get stories belonging to this epic
  const epicStories = parsed.stories.filter(s => s.epicId === epicId);
  
  // Check if epic has stories and deleteStories is not explicitly true
  if (epicStories.length > 0 && !options?.deleteStories) {
    throw new Error(`Epic ${epicId} has ${epicStories.length} stories. Set deleteStories: true to delete the epic and all its stories.`);
  }
  
  // Remove the epic
  parsed.epics.splice(epicIndex, 1);
  
  // Remove associated stories if deleteStories is true
  let deletedStories: UserStory[] = [];
  if (options?.deleteStories) {
    deletedStories = epicStories;
    parsed.stories = parsed.stories.filter(s => s.epicId !== epicId);
  }
  
  // Write back to file
  writeUserStoryFile(epic.filePath, parsed.epics, parsed.stories);
  
  return { epic: deletedEpic, stories: deletedStories };
}

